import { useSession } from "next-auth/react";
import { useCallback, useEffect, useState } from "react";
import {
  readLegalAcceptanceFromLocalStorage,
  readSoftDismissToken,
  writeLegalAcceptanceToLocalStorage,
  writeSoftDismissToken,
} from "./legalAcceptanceLocal";
import type { LegalAcceptanceStatusResponse } from "./legalAcceptanceTypes";
import { isLegalGateExemptPath } from "./legalPathExempt";
import { recordLegalAcceptanceOnClient } from "./recordLegalAcceptanceOnClient";
import { resolveLegalAcceptanceUiState } from "./resolveLegalAcceptanceUiState";

export function useLegalAcceptance(pathname: string) {
  const { status } = useSession();
  const sessionLoading = status === "loading";
  const exempt = isLegalGateExemptPath(pathname);
  const [published, setPublished] = useState<LegalAcceptanceStatusResponse | null>(null);
  const [fetchFailed, setFetchFailed] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [, setSoftDismissRevision] = useState(0);

  useEffect(() => {
    if (exempt || sessionLoading) {
      return;
    }
    let cancelled = false;
    (async () => {
      const response = await fetch("/api/legal/status");
      if (!response.ok || cancelled) {
        if (!cancelled) {
          console.error("Legal status request failed");
          setFetchFailed(true);
        }
        return;
      }
      const body = (await response.json()) as LegalAcceptanceStatusResponse;
      if (cancelled) {
        return;
      }
      setFetchFailed(false);
      setPublished(body);
      const signedIn = status === "authenticated";
      if (
        signedIn &&
        body.user?.termsAcceptedVersion &&
        body.user?.privacyPolicyAcceptedVersion
      ) {
        writeLegalAcceptanceToLocalStorage({
          termsVersion: body.user.termsAcceptedVersion,
          privacyVersion: body.user.privacyPolicyAcceptedVersion,
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [exempt, status]);

  const sessionIsSignedIn = status === "authenticated";

  const localPayload = readLegalAcceptanceFromLocalStorage();
  const softDismissToken = readSoftDismissToken();
  const { mode, dismissKey, isFirstAcceptancePrompt } =
    published && !exempt && !fetchFailed
      ? resolveLegalAcceptanceUiState(
          published,
          localPayload,
          sessionIsSignedIn,
          softDismissToken
        )
      : { mode: "ok" as const, dismissKey: "", isFirstAcceptancePrompt: false };

  const accept = useCallback(async () => {
    setAccepting(true);
    try {
      const ok = await recordLegalAcceptanceOnClient();
      if (ok) {
        const refreshed = await fetch("/api/legal/status");
        if (refreshed.ok) {
          setPublished((await refreshed.json()) as LegalAcceptanceStatusResponse);
        }
      }
    } finally {
      setAccepting(false);
    }
  }, []);

  const dismissSoft = useCallback(() => {
    writeSoftDismissToken(dismissKey);
    setSoftDismissRevision((revision) => revision + 1);
  }, [dismissKey]);

  return {
    exempt,
    // Never block the page behind a loading overlay: dialogs only after /api/legal/status returns.
    mode: exempt ? ("ok" as const) : mode,
    isFirstAcceptancePrompt: exempt ? false : isFirstAcceptancePrompt,
    accepting,
    accept,
    dismissSoft,
  };
}
